/*
 * Google Analytics 4, loaded only when a Measurement ID is configured.
 *
 * The ID comes from REACT_APP_GA_MEASUREMENT_ID, baked in at build time by the
 * deploy workflow from the GA_MEASUREMENT_ID environment variable. With no ID
 * set - local development, tests, or before the property exists - nothing is
 * requested, no cookies are set, and the privacy page says so.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const GA_MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();

/** GA4 Measurement IDs look like G-XXXXXXXXXX. Anything else is a misconfiguration. */
export const analyticsEnabled = /^G-[A-Z0-9]{4,}$/i.test(GA_MEASUREMENT_ID);

let initialized = false;

export function initAnalytics(): void {
  if (!analyticsEnabled || initialized || typeof document === 'undefined') return;
  initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  const gtag: (...args: unknown[]) => void = function gtag() {
    // gtag relies on `arguments` being pushed verbatim, not an array copy.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag = gtag;

  gtag('js', new Date());
  // This is a single-page app, so the automatic page_view would only ever fire
  // once. Turn it off and send one per route change instead - including the
  // first - so counts are right and nothing is double-reported.
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

export function trackPageView(path: string, title?: string): void {
  if (!analyticsEnabled || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}
